import React, { useRef, useState, useEffect } from "react";
import * as posenet from "@tensorflow-models/posenet";
import * as tf from "@tensorflow/tfjs";
import "@tensorflow/tfjs";
import "../css/Rom.css";

const Rom = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [romMessage, setRomMessage] = useState("No Detection Found.");
  const [detectedAngle, setDetectedAngle] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionComplete, setDetectionComplete] = useState(false);
  const [showCameraPrompt, setShowCameraPrompt] = useState(false);
  const [cameraError, setCameraError] = useState(false);

  const setupCamera = async () => {
    setShowCameraPrompt(true); // Show camera overlay when setting up the camera
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("Browser API navigator.mediaDevices.getUserMedia not available");
    }

    const video = videoRef.current;
    if (!video) return null;

    video.width = window.innerWidth;
    video.height = window.innerHeight;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: video.width, height: video.height },
      });
      video.srcObject = stream;
      setShowCameraPrompt(false); // Hide prompt if camera access succeeds

      return new Promise((resolve) => {
        video.onloadedmetadata = () => resolve(video);
      });
    } catch (error) {
      console.error("Error accessing camera: ", error);
      setCameraError(true); // Show error message if camera access fails
      return null;
    }
  };

  useEffect(() => {
    const loadPosenet = async () => {
      await tf.ready(); // Ensure TensorFlow.js is fully initialized
      const net = await posenet.load();
      const video = await setupCamera();
    
      if (video) {
        video.play();
        detectPoseInRealTime(video, net);
      } else {
        setShowCameraPrompt(true);
      }
    };
    

    loadPosenet();
  }, []);

  const retryCameraAccess = async () => {
    setCameraError(false); // Reset error state
    const video = await setupCamera(); // Try to set up the camera again
    if (video) {
      video.play(); // Play the video if successful
    } else {
      setShowCameraPrompt(true); // Show prompt if camera setup fails
    }
  };

  const detectPoseInRealTime = (video, net) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const poseDetectionFrame = async () => {
      const pose = await net.estimateSinglePose(video, { flipHorizontal: false });

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      detectRightLeg(pose, ctx);

      requestAnimationFrame(poseDetectionFrame);
    };
    poseDetectionFrame();
  };

  const detectRightLeg = (pose, ctx) => {
    const keypoints = pose.keypoints;
  
    const rightHip = keypoints.find((point) => point.part === "rightHip");
    const rightKnee = keypoints.find((point) => point.part === "rightKnee");
    const rightAnkle = keypoints.find((point) => point.part === "rightAnkle");
  
    const kneeConfidenceThreshold = 0.5; // Adjusted threshold
    const ankleConfidenceThreshold = 0.5; // Adjusted threshold
  
    if (
      rightKnee && rightKnee.score > kneeConfidenceThreshold &&
      rightAnkle && rightAnkle.score > ankleConfidenceThreshold
    ) {
      if (!isDetecting) {
        setIsDetecting(true);
        setTimeout(() => {
          setIsDetecting(false);
          setDetectionComplete(true);
        }, 5000);
      }
  
      if (rightKnee && rightHip) {
        let angle = calculateAngle(rightHip.position, rightKnee.position, rightAnkle.position);
  
        // Round the angle to the nearest 5 for stability
        angle = Math.round(angle);
  
        if (angle >= 0 && angle !== detectedAngle) {
          setDetectedAngle(angle);
          updateRomMessage(angle);
          drawAngleLine(ctx, rightHip.position, rightKnee.position, rightAnkle.position);
        }
      }
    } else {
      setRomMessage("No Detection Found.");
      setDetectedAngle(null);
      setIsDetecting(false);
    }
  };
  

  const updateRomMessage = (angle) => {
    if (angle < 83) {
      setRomMessage("You're getting there! Keep working towards 83° to climb stairs safely.");
    } else if (angle >= 83 && angle <= 89) {
      setRomMessage("Awesome! You can climb stairs now.");
    } else if (angle === 90) {
      setRomMessage("Great job! You've reached 90°, safe to descend stairs.");
    } else if (angle >= 91 && angle <= 104) {
      setRomMessage("You're almost there! Keep going.");
    } else if (angle === 105) {
      setRomMessage("Amazing! Standing up from a chair is easier.");
    } else if (angle >= 106 && angle <= 114) {
      setRomMessage("You're getting close to biking time.");
    } else if (angle === 115) {
      setRomMessage("Fantastic! You’re ready to ride a bike!");
    } else if (angle >= 116 && angle <= 134) {
      setRomMessage("Keep pushing, full flexion is right around the corner.");
    } else if (angle === 135) {
      setRomMessage("Full flexion achieved!");
    }
  };

  const drawAngleLine = (ctx, hip, knee, ankle) => {
    const videoWidth = canvasRef.current.width;
    const videoHeight = canvasRef.current.height;
    const scaleX = videoWidth / window.innerWidth;
    const scaleY = videoHeight / window.innerHeight;

    const scaledHip = { x: hip.x * scaleX, y: hip.y * scaleY };
    const scaledKnee = { x: knee.x * scaleX, y: knee.y * scaleY };
    const scaledAnkle = { x: ankle.x * scaleX, y: ankle.y * scaleY };

    ctx.strokeStyle = "rgba(21, 169, 207, 0.70)"; // Line color
    ctx.lineWidth = 4; // Increased line thickness
    ctx.beginPath();
    ctx.moveTo(scaledHip.x, scaledHip.y);
    ctx.lineTo(scaledKnee.x, scaledKnee.y);
    ctx.lineTo(scaledAnkle.x, scaledAnkle.y);
    ctx.stroke();

    const drawCircle = (x, y) => {
      ctx.fillStyle = "#FC6448"; // Circle fill color
      ctx.strokeStyle = "rgba(255, 255, 255, 0.70)"; // Circle stroke color
      ctx.lineWidth = 2; // Circle stroke width

      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2); // Reduced circle radius
      ctx.fill();
      ctx.stroke();
    };

    drawCircle(scaledHip.x, scaledHip.y); // Right Hip
    drawCircle(scaledKnee.x, scaledKnee.y); // Right Knee
    drawCircle(scaledAnkle.x, scaledAnkle.y); // Right Ankle

    ctx.font = "18px Arial";
    ctx.fillStyle = "blue";
    ctx.fillText(`${Math.round(calculateAngle(scaledHip, scaledKnee, scaledAnkle))}°`, scaledKnee.x, scaledKnee.y - 10);
  };

  const calculateAngle = (hip, knee, ankle) => {
    const dy1 = hip.y - knee.y;
    const dx1 = hip.x - knee.x;
    const dy2 = ankle.y - knee.y;
    const dx2 = ankle.x - knee.x;

    const angleRad = Math.atan2(dy2, dx2) - Math.atan2(dy1, dx1);
    let angleDeg = Math.abs((angleRad * 180) / Math.PI);

    if (angleDeg > 180) angleDeg = 360 - angleDeg;

    angleDeg = 180 - angleDeg;

    return angleDeg;
  };

  const getSpriteSrc = () => {
    if (detectedAngle === null || !detectedAngle) {
      return "../rom-sprites/NoDetectionFound.svg"; // Fallback image
    } else if (detectedAngle < 20) {
      return "../rom-sprites/Right Knee-1.png";
    } else if (detectedAngle < 60) {
      return "../rom-sprites/Right Knee-2.png";
    } else if (detectedAngle < 83) {
      return "../rom-sprites/Right Knee-3.png";
    } else if (detectedAngle <= 90) {
      return "../rom-sprites/Right Knee-4.png";
    } else if (detectedAngle <= 105) {
      return "../rom-sprites/Right Knee-5.png";
    } else if (detectedAngle <= 115) {
      return "../rom-sprites/Right Knee-6.png";
    } else if (detectedAngle <= 134) {
      return "../rom-sprites/Right Knee-7.png";
    } else {
      return "../rom-sprites/Right Knee-8.png";
    }
  };

  return (
    <div className="divContainer">
      {showCameraPrompt && (
        <div className="camera-overlay">
          <img className="top-right" src="../top-circle.png" />
          <img className="bottom-left" src="../bottom-circle.png" />
          <div className="setup">
            <h2>Set up your device</h2>
            <p>Allow your camera to track your <br />movement</p>
            <img src="../ROM Illustration.png" width='300' />
            <button onClick={retryCameraAccess}>Continue</button>
          </div>
        </div>
      )}
      {cameraError && (
        <div className="camera-error-overlay">
          <div className="camera-overlay">
            <img className="top-right" src="../top-circle.png" />
            <img className="bottom-left" src="../bottom-circle.png" />
            <div className="setup">
              <h2>Set up your device</h2>
              <p>Camera access is required. Please allow <br />camera access and click Retry.</p>
              <img src="../ROM Illustration.png" width='300' />
              <button onClick={retryCameraAccess}>Retry</button>
            </div>
          </div>
        </div>
      )}
      <div className="customRomHeader">
        <a href="/">
          <img src='../menuIconRom.svg' width='27' alt="Menu Icon" />
          <img src='../Engivio Login Logo.svg' width='123' alt="Engivio Logo" />
        </a>
      </div>
      <video ref={videoRef} style={{ display: "none" }} autoPlay playsInline muted />
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", border: "1px solid black" }} />
      <div className="sideData">
        <div className="romAngle">
          <p>Angle: <span className="angle">{detectedAngle !== null ? detectedAngle : 0}°</span></p>
        </div>
        <div className="romMovement">
          <img src={getSpriteSrc()} style={{ transition: "opacity 0.5s ease-in-out" }} alt="Knee Movement" />
          <p>{romMessage}</p>
        </div>
      </div>

      {isDetecting && <div className="progress-bar">Detecting...</div>}
      {detectionComplete && <div className="notification-overlay">Detection complete!</div>}
    </div>
  );
};

export default Rom;
